import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "styled-components";
import { darkTheme, lightTheme, GlobalStyle } from "./theme";

export type ThemeMode = "light" | "dark";

type ThemeModeValue = {
  mode: ThemeMode;
  toggle: () => void;
};

const ThemeModeContext = createContext<ThemeModeValue>({ mode: "dark", toggle: () => undefined });

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

export function AppProviders({ children }: PropsWithChildren) {
  const [mode, setMode] = useState<ThemeMode>("dark");

  useEffect(() => {
    const stored = window.localStorage.getItem("tpeck-theme") as ThemeMode | null;
    const preferred = window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
    setMode(stored || preferred);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    window.localStorage.setItem("tpeck-theme", mode);
  }, [mode]);

  const value = useMemo<ThemeModeValue>(() => ({
    mode,
    toggle: () => setMode(current => current === "dark" ? "light" : "dark")
  }), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={mode === "dark" ? darkTheme : lightTheme}>
        <GlobalStyle />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
